import React from 'react'

import { Progress } from '@chakra-ui/react'
import {
  HStack,
  IconButton,
  Stack,
  Tag,
  TColumnDef,
  TTagVariant,
  Typography,
} from '@pnld/components-web'

import { Processing, Status } from '@/types/processing'

import {
  DEED_TYPE_LABEL_WITH_LEGACY,
  EDUCATION_STAGE_LABEL,
  STATUS_LABEL,
} from './constants'

const STATUS_TAG_VARIANT: Record<Status, TTagVariant> = {
  [Status.PENDENTE]: 'neutral',
  [Status.PROCESSANDO]: 'primary',
  [Status.CONCLUIDO]: 'positive',
  [Status.FALHOU]: 'negative',
}

const PROGRESS_COLOR: Record<Status, string> = {
  [Status.PENDENTE]: 'blue',
  [Status.PROCESSANDO]: 'blue',
  [Status.CONCLUIDO]: 'green',
  [Status.FALHOU]: 'red',
}

type ProcessingCellProps = { processing: Processing }

type ProcessingColumnHandlers = {
  onView: (processing: Processing) => void
  onShowErrorLog: (processing: Processing) => void
}

const CenteredHeader: React.FC<{ label: string }> = ({ label }) => (
  <Stack alignItems="center" justifyContent="center">
    <Typography variant="s-xs">{label}</Typography>
  </Stack>
)

const NoticeCell: React.FC<ProcessingCellProps> = ({ processing }) => (
  <Stack alignItems="center" justifyContent="center">
    <Typography variant="b-sm">{processing.dsNotice || '—'}</Typography>
  </Stack>
)

const StatusCell: React.FC<ProcessingCellProps> = ({ processing }) => (
  <Stack alignItems="center" justifyContent="center">
    <Tag
      variant={STATUS_TAG_VARIANT[processing.stStatus]}
      sx={{
        textWrap: 'nowrap',
      }}
    >
      {STATUS_LABEL[processing.stStatus]}
    </Tag>
  </Stack>
)

const ProgressCell: React.FC<ProcessingCellProps> = ({ processing }) => {
  const { stStatus, nuProgress } = processing
  if (stStatus === Status.PENDENTE) return null

  const isAnimated = stStatus === Status.PROCESSANDO

  return (
    <Stack minW="100px" gap={1}>
      <Progress
        value={nuProgress}
        colorScheme={PROGRESS_COLOR[stStatus]}
        hasStripe={isAnimated}
        isAnimated={isAnimated}
        borderRadius="full"
        size="sm"
      />
      <Typography variant="b-sm" textAlign="center">
        {nuProgress}%
      </Typography>
    </Stack>
  )
}

const ActionsCell: React.FC<ProcessingCellProps & ProcessingColumnHandlers> = ({
  processing,
  onView,
  onShowErrorLog,
}) => (
  <HStack alignItems="center" justifyContent="center" spacing={0}>
    <IconButton
      aria-label="Visualizar"
      iconName="eye"
      variant="tertiary"
      onClick={() => onView(processing)}
      disabled={
        processing.stStatus !== Status.CONCLUIDO &&
        processing.stStatus !== Status.FALHOU
      }
    />
    {processing.stStatus === Status.FALHOU && (
      <IconButton
        aria-label="Ver detalhes da falha"
        iconName="bug"
        variant="tertiary"
        onClick={() => onShowErrorLog(processing)}
        disabled={!processing.dsErrorLog}
      />
    )}
  </HStack>
)

const formatCreatedAt = (value: string) =>
  new Date(value).toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

// Sem isto o DataGrid identifica a linha pela posição, e um item novo no topo
// herdaria o estado da linha que estava ali (o foco, por exemplo).
export const getProcessingRowId = (processing: Processing) =>
  String(processing.coProcessing)

// O DataGrid renderiza `header`/`cell` como componentes: uma função nova a
// cada render remonta a célula. Chame com handlers estáveis e memorize.
export const buildProcessingColumns = ({
  onView,
  onShowErrorLog,
}: ProcessingColumnHandlers): Array<TColumnDef<Processing>> => [
  {
    accessorKey: 'coProcessing',
    header: 'ID',
    enableSorting: false,
  },
  {
    accessorKey: 'dsNotice',
    header: () => <CenteredHeader label="Edital" />,
    enableSorting: false,
    cell: ({ row }) => <NoticeCell processing={row.original} />,
  },
  {
    accessorKey: 'nuDeedType',
    header: 'Tipo de obra',
    enableSorting: false,
    cell: ({ row }) => DEED_TYPE_LABEL_WITH_LEGACY[row.original.nuDeedType],
  },
  {
    accessorKey: 'nuEducationStage',
    header: 'Etapa de ensino',
    enableSorting: false,
    cell: ({ row }) => EDUCATION_STAGE_LABEL[row.original.nuEducationStage],
  },
  {
    accessorKey: 'dtCreatedAt',
    header: 'Data',
    enableSorting: false,
    cell: ({ row }) => formatCreatedAt(row.original.dtCreatedAt),
  },
  {
    id: 'status',
    header: () => <CenteredHeader label="Status" />,
    enableSorting: false,
    cell: ({ row }) => <StatusCell processing={row.original} />,
  },
  {
    id: 'progress',
    header: () => <CenteredHeader label="Progresso" />,
    enableSorting: false,
    cell: ({ row }) => <ProgressCell processing={row.original} />,
  },
  {
    id: 'actions',
    header: () => <CenteredHeader label="Ações" />,
    enableSorting: false,
    cell: ({ row }) => (
      <ActionsCell
        processing={row.original}
        onView={onView}
        onShowErrorLog={onShowErrorLog}
      />
    ),
  },
]
